/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import { FormProvider, useForm, type SubmitHandler } from 'react-hook-form'
import FormInput from '../../../components/FormElements/FormInput'
import { yupResolver } from '@hookform/resolvers/yup'
import { useAppDispatch } from '../../../store'
import * as yup from 'yup'
import { useTheme } from '@mui/material/styles'
import { type ReportHierarchyRowData } from '../types'
import { type Division } from '../../../models/Division'
import { createDivision, editDivision, getBreadcrumbList } from '../../../store/slices/reportHierarchySlice/apis'
import { divisionDetails, isCreateEditDivisionLoading } from '../../../store/slices/reportHierarchySlice/selectors'
import ValidationAlert from '../../../components/ValidationAlert'
import { bindSetError, showServerFieldErrors } from '../../../utils/showServerFieldErrors'
import DefaultModalActions from '../../../components/GenericModal/DefaultModalActions'
import { getDirtyValues } from '../../../utils/onlyUpdatedFields'
import { LastUpdateMeta } from '../../../components/GenericModal/ChangeMetaInfo'
import { useParams } from 'react-router-dom'
import { Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useAccess } from '../../../hooks/useAccess'
import { READONLY_TOOLTIP } from '../../../consts'
import { grey } from '../../../colors'

interface FormType {
  title: string
  abbreviation: string
  categoryName: string
  parent: string
  institutionId?: string | null
  integrationSourceId?: string | null
}

interface CreateEditDivisionProps {
  selectedRow?: Division
  selectedRowWithActions?: any
  handleCloseModal?: () => void
  isTaskRunning?: boolean
  onTaskBlock?: () => void
}

export const CreateEditDivision: React.FC<CreateEditDivisionProps> = ({
  selectedRow,
  selectedRowWithActions,
  handleCloseModal = () => { },
  isTaskRunning,
  onTaskBlock
}: CreateEditDivisionProps): JSX.Element => {
  const { readonly } = useAccess(selectedRowWithActions?.actions ?? [])
  const [errors, setErrors] = useState<unknown>({})
  const division = divisionDetails()

  useEffect(() => {
    if (selectedRow != null && isTaskRunning) {
      onTaskBlock?.()
      handleCloseModal()
    }
  }, [selectedRow])
  const theme = useTheme()
  const dispatch = useAppDispatch()
  const isEditing = selectedRow
  const isEditAction = Boolean(isEditing)
  const loading = isCreateEditDivisionLoading()
  const { id } = useParams()
  const { title, abbreviation, categoryName, parent } = selectedRow ?? {}

  const distributionFormSchema = yup.object().shape({
    title: yup.string().required('Title is required'),
    abbreviation: yup.string().required('Abbreviation is required'),
    categoryName: yup.string().required('Unit type is required'),
    parent: yup.string().required('Parent Unit is required'),
    institutionId: yup.string().notRequired().nullable(),
    integrationSourceId: yup.string().notRequired().nullable()
  })

  const defaultParent: Division | null = division?.data ?? null
  const defaultCategoryName = division?.data?.childrenCategory?.childCategoryName ?? ''
  const defaultCategoryId = division?.data?.childrenCategory?.childCategoryId ?? null

  const methods = useForm<FormType>({
    resolver: yupResolver(distributionFormSchema),
    defaultValues: {
      title: title ?? '',
      abbreviation: abbreviation ?? '',
      categoryName: categoryName ?? defaultCategoryName,
      parent: parent?.title ?? defaultParent?.title,
      institutionId: selectedRow?.institutionId ?? null,
      integrationSourceId: selectedRow?.integrationSourceId ?? null
    }
  })
  const { handleSubmit, control, formState: { isDirty, dirtyFields, isSubmitting } } = methods

  const onSubmit: SubmitHandler<FormType> = async (data: FormType, e: any) => {
    try {
      const { institutionId, integrationSourceId, ...restData } = data
      if (isEditing) {
        const updatedVals = getDirtyValues(dirtyFields, { ...restData, institutionId, integrationSourceId })
        await dispatch(editDivision({ urlId: selectedRow?.divisionId, data: updatedVals })).unwrap()
        await dispatch(getBreadcrumbList({ urlId: id ?? '' }))
      } else {
        const createdData = {
          ...restData,
          institutionId,
          integrationSourceId,
          categoryId: defaultCategoryName,
          parent: parent?.divisionId ?? defaultParent?.divisionId
        }
        await dispatch(createDivision({ data: createdData })).unwrap()
      }

      handleCloseModal?.()
    } catch (error) {
      setErrors(showServerFieldErrors(error, bindSetError(methods.setError), methods.clearErrors))
    }
  }

  return (
    <FormProvider {...methods}>
      <ValidationAlert errors={errors} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box display='flex' flexDirection='column' gap={theme.spacing(6)}>
          <FormInput
            name="title"
            label="Title"
            control={control}
            required
          />
          <FormInput
            name="abbreviation"
            label="Abbreviation"
            control={control}
            required
          />
          <FormInput
            name="categoryName"
            label="Academic Unit"
            control={control}
            required
            disabled
          />
          <FormInput
            name="parent"
            label="Parent Unit "
            control={control}
            required
            disabled
          />
          <Accordion disableGutters elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={600}>Additional Fields</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box display='flex' flexDirection='column' gap={theme.spacing(4)}>
                <Box>
                  <FormInput name="institutionId" defaultValue={selectedRow?.institutionId ?? undefined} label="Institution ID" control={control} placeholder='Institution ID' />
                  <Typography fontSize="14px" mt={1} color={grey[850]}>
                    Identifier from your SIS or data warehouse. Changing this may break syncing with your institutional data.
                  </Typography>
                </Box>
                <Box>
                  <FormInput name="integrationSourceId" defaultValue={selectedRow?.integrationSourceId ?? undefined} label="Integration Source ID" control={control} placeholder='Integration Source ID' />
                  <Typography fontSize="14px" mt={1} color={grey[850]}>
                    Identifier from an external system (e.g. LMS). Changing this may break the connection to that system and any related functionality.
                  </Typography>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>

        <DefaultModalActions
          destructiveButtonProps={{
            onClick: handleCloseModal
          }}
          confirmButtonProps={{
            text: isEditing ? 'Save' : 'Create',
            loading,
            type: 'submit',
            disabled: readonly || isSubmitting || (isEditing && !isDirty),
            tooltip: readonly ? READONLY_TOOLTIP : undefined
          }}

          lastUpdateMeta={isEditAction && (
            <LastUpdateMeta lastEditedBy={selectedRow?.modifiedByUsername} lastEditedAt={selectedRow?.modifiedOn} />
          )}

        />
      </form>
    </FormProvider>
  )
}

export default CreateEditDivision
